import React, { useEffect, useState } from 'react';
import FloatingChatButton from './FloatingChatButton';
import DockableChatBox from './DockableChatBox';
import SessionPromptDialog from './SessionPromptDialog';
import { chatApi } from 'src/api/chatApi';
import useAuth from 'src/hooks/useAuth';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import {
    openChatBox,
    closeChatBox,
    toggleMinimize,
    setLastOpenSession,
    clearSessionToReopen,
} from 'src/store/slices/chatWidgetSlice';
import FeedbackCollection from './FeedbackCollection';
import { Modal } from '@material-ui/core';
import { FeedbackData } from 'src/types/chat';

interface ChatWidgetProps {
    // Optional props for customization
    disabled?: boolean;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ disabled = false }) => {
    const dispatch = useAppDispatch();
    const { showChatBox, isMinimized, lastOpenSession, sessionToReopen } = useAppSelector(state => state.chatWidget);
    const [unreadCount] = useState(0);
    const [showSessionPrompt, setShowSessionPrompt] = useState(false);
    const [sessionActionLoading, setSessionActionLoading] = useState(false);
    const [showFeedbackCollection, setShowFeedbackCollection] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const fetchLastOpenSession = async () => {
            const response = await chatApi.getLastOpenSession(user.id);
            dispatch(setLastOpenSession(response.data));
        };
        fetchLastOpenSession();
    }, [dispatch, user.id]);

    // Effect to handle session reopening from other components
    useEffect(() => {
        if (sessionToReopen) {
            // Clear the session to reopen flag
            dispatch(clearSessionToReopen());
            toast.success('Session re-opened');
        }
    }, [sessionToReopen, dispatch]);

    const handleFeedbackClose = () => {
        // Clear any pending actions when feedback is cancelled
        sessionStorage.removeItem('pendingAction');
        setShowFeedbackCollection(false);
    };
    const handleFeedbackSubmit = async (feedbackData: FeedbackData) => {
        try {
            await chatApi.submitFeedback(feedbackData);
            toast.success('Feedback submitted');

            // Get the pending action and execute it
            const pendingAction = sessionStorage.getItem('pendingAction');

            if (pendingAction === 'createNewSameCase' && lastOpenSession) {
                // Close the existing session and create new session for same case
                await chatApi.updateSessionStatus(lastOpenSession.id, 'CLOSE');
                toast.success('Previous session closed and new session created for the same case');

                // Create a new session with the same case information
                const newSessionPayload = {
                    projectId: lastOpenSession.projectId,
                    orgId: lastOpenSession.orgId,
                    customerId: lastOpenSession.customerId,
                    caseKey: lastOpenSession.caseKey,
                };

                // Create the new session
                const response = await chatApi.createSession(newSessionPayload);
                dispatch(setLastOpenSession(response.data.data));
                setShowSessionPrompt(false);
                dispatch(openChatBox());

            } else if (pendingAction === 'createNewSession' && lastOpenSession) {
                // Close the existing session first
                await chatApi.updateSessionStatus(lastOpenSession.id, 'CLOSE');
                toast.success('Previous session closed');

                // Check again for any remaining open sessions after closing the current one
                try {
                    const response = await chatApi.getLastOpenSession(user.id);
                    const remainingOpenSession = response.data;

                    if (remainingOpenSession && remainingOpenSession.id !== lastOpenSession?.id) {
                        // There are still open sessions, update the state and show the prompt again
                        dispatch(setLastOpenSession(remainingOpenSession));
                        // Keep the session prompt open to show the next open session     
                    } else {
                        // No more open sessions, proceed to create new session
                        dispatch(setLastOpenSession(null));
                        setShowSessionPrompt(false);
                        dispatch(openChatBox());
                        toast.success('Ready to create new session');
                    }
                } catch (sessionCheckError) {
                    // If checking for remaining sessions fails, proceed with creating new session
                    console.error('Error checking for remaining sessions:', sessionCheckError);
                    dispatch(setLastOpenSession(null));
                    setShowSessionPrompt(false);
                    dispatch(openChatBox());
                    toast.success('Ready to create new session');
                }
            }

            // Clear the pending action
            sessionStorage.removeItem('pendingAction');

        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback');
        } finally {
            setShowFeedbackCollection(false);
        }
    };

    const handleFloatingButtonClick = async () => {

        try {
            const response = await chatApi.getLastOpenSession(user.id);
            const latestSession = response.data;

            // Update the state with the latest session info
            dispatch(setLastOpenSession(latestSession));

            if (!latestSession) {
                // No open session found, open chat with normal flow (category → project → description → session)
                setShowSessionPrompt(false);
                dispatch(openChatBox());
            } else {
                // Found an open session, show the session prompt dialog
                setShowSessionPrompt(true);
            }
        } catch (error) {
            console.error('Error fetching last open session:', error);
            // On error, proceed to open chat directly
            dispatch(openChatBox());
        }
    };

    const handleCloseChatBox = () => {
        dispatch(closeChatBox());
    };

    const handleChatMinimize = () => {
        dispatch(toggleMinimize());
    };


    const handleJoinExistingSession = async () => {
        setSessionActionLoading(true);
        try {
            // Close the dialog and open chat with existing session
            setShowSessionPrompt(false);
            dispatch(openChatBox());
            toast.success('Joined existing session');
        } catch (error) {
            toast.error('Failed to join existing session');
            console.error('Error joining existing session:', error);
        } finally {
            setSessionActionLoading(false);
        }
    };
    const handleCreateNewSameCase = async () => {
        setSessionActionLoading(true);
        try {

            if (lastOpenSession) {
                // Show feedback collection before closing the session
                if (lastOpenSession.supportUsers.length > 0) {
                    setShowFeedbackCollection(true);
                }
                else {
                    await chatApi.updateSessionStatus(lastOpenSession.id, 'CLOSE');
                    const newSessionPayload = {
                        projectId: lastOpenSession.projectId,
                        orgId: lastOpenSession.orgId,
                        customerId: lastOpenSession.customerId,
                        caseKey: lastOpenSession.caseKey,
                    };

                    // Create the new session
                    const response = await chatApi.createSession(newSessionPayload);
                    dispatch(setLastOpenSession(response.data.data));
                    setShowSessionPrompt(false);
                    dispatch(openChatBox());

                }
                // setShowFeedbackCollection(true);
                setSessionActionLoading(false);

                // Store the action to perform after feedback collection
                sessionStorage.setItem('pendingAction', 'createNewSameCase');
            } else {
                toast.error('No existing session found');
                setSessionActionLoading(false);
            }
        } catch (error) {
            toast.error('Failed to create new session for the same case');
            console.error('Error creating new session for same case:', error);
            setSessionActionLoading(false);
        }
    }

    const handleCreateNewSession = async () => {
        setSessionActionLoading(true);
        try {
            if (lastOpenSession) {
                // Show feedback collection before closing the session
                console.log('lastOpenSession', lastOpenSession);
                if (lastOpenSession.supportUsers.length > 0) {
                    setShowFeedbackCollection(true);
                }
                else {
                    await chatApi.updateSessionStatus(lastOpenSession.id, 'CLOSE');
                    const response = await chatApi.getLastOpenSession(user.id);
                    const remainingOpenSession = response.data;
                    if (remainingOpenSession && remainingOpenSession.id !== lastOpenSession?.id) {
                        // There are still open sessions, update the state and show the prompt again
                        dispatch(setLastOpenSession(remainingOpenSession));
                        // Keep the session prompt open to show the next open session     
                    } else {
                        // No more open sessions, proceed to create new session
                        dispatch(setLastOpenSession(null));
                        setShowSessionPrompt(false);
                        dispatch(openChatBox());
                        toast.success('Ready to create new session');
                    }
                }
                // setShowFeedbackCollection(true);
                setSessionActionLoading(false);

                // Store the action to perform after feedback collection
                sessionStorage.setItem('pendingAction', 'createNewSession');
            } else {
                // No existing session, proceed to create new session directly
                dispatch(setLastOpenSession(null));
                setShowSessionPrompt(false);
                dispatch(openChatBox());
                toast.success('Ready to create new session');
                setSessionActionLoading(false);
            }
        } catch (error) {
            toast.error('Failed to close existing session');
            console.error('Error closing session:', error);
            setSessionActionLoading(false);
        }
    };

    const handleCloseSessionPrompt = () => {
        setShowSessionPrompt(false);
    };

    // Don't render anything if disabled
    if (disabled) {
        return null;
    }

    return (
        <>
            {/* Show floating button when chat box is not open or when it's minimized */}
            {(!showChatBox || isMinimized) && (
                <FloatingChatButton
                    onClick={handleFloatingButtonClick}
                    unreadCount={unreadCount}
                    disabled={disabled}
                />
            )}

            {/* Dockable Chat Box - shows normal flow (category→project→description) when no existingSession */}
            {showChatBox && (
                <DockableChatBox
                    onClose={handleCloseChatBox}
                    onMinimize={handleChatMinimize}
                    existingSession={sessionToReopen || lastOpenSession}
                />
            )}

            {/* Session Prompt Dialog */}

            <SessionPromptDialog
                open={showSessionPrompt}
                onClose={handleCloseSessionPrompt}
                onCreateNewSameCase={handleCreateNewSameCase}
                onJoinExisting={handleJoinExistingSession}
                onCreateNew={handleCreateNewSession}
                lastOpenSession={lastOpenSession}
                loading={sessionActionLoading}
            />
            <Modal
                open={showFeedbackCollection}
                onClose={handleFeedbackClose}
            >
                <FeedbackCollection
                    orgId={lastOpenSession?.orgId || ''}
                    projectId={lastOpenSession?.projectId || ''}
                    customerId={lastOpenSession?.customerId || ''}
                    caseKey={lastOpenSession?.caseKey || ''}
                    supportUserId={lastOpenSession?.supportUsers?.[0]?.id || ''}
                    chatSessionId={lastOpenSession?.id || ''}
                    onSubmit={handleFeedbackSubmit}
                    onClose={handleFeedbackClose}
                />
            </Modal>
        </>
    );
};

export default ChatWidget; 